import { useCallback } from 'react';
import { getRestaurantColorSettings, saveRestaurantColorSettings } from '../../../../../api/restaurantColors';
import { ColorCategoryId } from '../../designPageTypes';
import {
  createDefaultColorRows,
  fromApiColorSettings,
  isValidColorPayload,
  normalizeHex,
  toApiColorSettings
} from '../../designPageUtils';
import useDesignTabSettings, { DesignTabLoadActions } from '../../hooks/useDesignTabSettings';

type ColorState = Record<ColorCategoryId, string>;
type ColorPayload = ReturnType<typeof toApiColorSettings>;

const useDesignColorSettings = (isActive: boolean) => {
  const loadColorSettings = useCallback(
    async (
      _restaurantId: number,
      { setState, setSettingsError, markPersisted, setSettingsReady }: DesignTabLoadActions<ColorState, ColorPayload>
    ) => {
      try {
        const colorSettingsResult = await getRestaurantColorSettings();
        const loadedColorRows = fromApiColorSettings(colorSettingsResult.colors);
        setState(loadedColorRows);
        markPersisted(toApiColorSettings(loadedColorRows));
        setSettingsReady(true);
      } catch {
        markPersisted(toApiColorSettings(createDefaultColorRows()));
        setSettingsError('Unable to load saved color settings. You can still edit and save below.');
        setSettingsReady(true);
      }
    },
    []
  );

  const {
    settingsLoading,
    settingsError,
    state: colors,
    setState: setColors
  } = useDesignTabSettings<ColorState, ColorPayload>({
    isActive,
    initialState: createDefaultColorRows,
    toPayload: toApiColorSettings,
    save: {
      toastId: 'design-color-settings',
      savingMessage: 'Saving color settings…',
      savedMessage: 'Color settings saved',
      errorMessage: 'Could not save color settings',
      isValid: isValidColorPayload,
      save: saveRestaurantColorSettings
    },
    load: loadColorSettings
  });

  const setSiteColor = useCallback(
    (id: ColorCategoryId, color: string) => {
      const normalized = normalizeHex(color);
      setColors((prev) => ({
        ...prev,
        [id]: normalized ?? color
      }));
    },
    [setColors]
  );

  return {
    settingsLoading,
    colorSettingsError: settingsError,
    colors,
    setSiteColor
  };
};

export default useDesignColorSettings;
